/**
 * OpsAgent Panel — anomaly feed, incident timeline, dependencies, probes
 * Phase 4.8-B — WQ-200
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  getSummary,
  listAnomalies,
  listIncidents,
  listDependencies,
  listProbes,
  resolveAnomaly,
  type OpsAgentSummary,
  type Anomaly,
  type Incident,
  type Dependency,
  type Probe,
} from '../../services/opsAgentService';
import { fmt, fmtPct } from '../../utils/formatNumber';

const SEV_COLORS: Record<string, string> = {
  low: '#3b82f6',
  medium: '#f59e0b',
  high: '#f97316',
  critical: '#ef4444',
};

const STATUS_COLORS: Record<string, string> = {
  open: '#ef4444',
  acknowledged: '#f59e0b',
  resolved: '#22c55e',
  up_to_date: '#22c55e',
  outdated: '#f59e0b',
  eol: '#ef4444',
  unknown: '#6b7280',
};

type ViewTab = 'anomalies' | 'incidents' | 'dependencies' | 'probes';

const OpsAgentPanel: React.FC = () => {
  const { authFetch, isLoading: authLoading } = useAuth();
  const [summary, setSummary] = useState<OpsAgentSummary | null>(null);
  const [anomalies, setAnomalies] = useState<Anomaly[]>([]);
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [dependencies, setDependencies] = useState<Dependency[]>([]);
  const [probes, setProbes] = useState<Probe[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewTab, setViewTab] = useState<ViewTab>('anomalies');
  const [resolvingId, setResolvingId] = useState<string | null>(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [summ, anom, inc, deps, prbs] = await Promise.all([
        getSummary(authFetch),
        listAnomalies(authFetch, { is_resolved: false, limit: 30 }),
        listIncidents(authFetch, { limit: 20 }),
        listDependencies(authFetch),
        listProbes(authFetch),
      ]);
      setSummary(summ);
      setAnomalies(Array.isArray(anom) ? anom : []);
      setIncidents(Array.isArray(inc) ? inc : []);
      setDependencies(Array.isArray(deps) ? deps : []);
      setProbes(Array.isArray(prbs) ? prbs : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load OpsAgent data');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!authLoading) fetchData();
  }, [authLoading]);

  const handleResolveAnomaly = async (id: string) => {
    setResolvingId(id);
    try {
      await resolveAnomaly(authFetch, id);
      setAnomalies(prev => prev.filter(a => a.id !== id));
    } catch { /* keep in list */ }
    setResolvingId(null);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
        <div style={{ width: 32, height: 32, border: '3px solid rgba(255,255,255,0.1)', borderTop: '3px solid #00d4ff', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="glass" style={{ padding: 20, textAlign: 'center' }}>
        <p style={{ color: '#ef4444', marginBottom: 12 }}>{error}</p>
        <button onClick={fetchData} className="glass" style={{ padding: '6px 16px', cursor: 'pointer', color: '#00d4ff', background: 'rgba(0,212,255,0.1)', border: '1px solid rgba(0,212,255,0.3)', borderRadius: 6 }}>
          Retry
        </button>
      </div>
    );
  }

  const tabs: { key: ViewTab; label: string }[] = [
    { key: 'anomalies', label: `Anomalies${summary ? ` (${summary.anomalies.unresolved})` : ''}` },
    { key: 'incidents', label: `Incidents${summary ? ` (${summary.open_incidents})` : ''}` },
    { key: 'dependencies', label: `Dependencies${summary ? ` (${summary.dependencies.total})` : ''}` },
    { key: 'probes', label: `Probes${summary ? ` (${summary.probes.active})` : ''}` },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* KPIs */}
      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
          <div className="glass" style={{ padding: 16, textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: summary.anomalies.unresolved > 0 ? '#f59e0b' : '#22c55e' }}>
              {summary.anomalies.unresolved}
            </div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>Unresolved Anomalies</div>
          </div>
          <div className="glass" style={{ padding: 16, textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: summary.open_incidents > 0 ? '#ef4444' : '#22c55e' }}>
              {summary.open_incidents}
            </div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>Open Incidents</div>
          </div>
          <div className="glass" style={{ padding: 16, textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: summary.dependencies.eol > 0 ? '#ef4444' : '#22c55e' }}>
              {summary.dependencies.outdated}
            </div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>Outdated Deps</div>
          </div>
          <div className="glass" style={{ padding: 16, textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: summary.probes.failing > 0 ? '#ef4444' : '#22c55e' }}>
              {summary.probes.failing}
            </div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>Failing Probes</div>
          </div>
        </div>
      )}

      {/* View Tabs */}
      <div style={{ display: 'flex', gap: 4, background: 'rgba(255,255,255,0.03)', borderRadius: 8, padding: 4 }}>
        {tabs.map(t => (
          <button key={t.key}
            onClick={() => setViewTab(t.key)}
            style={{
              flex: 1, padding: '8px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 500,
              background: viewTab === t.key ? 'rgba(0,212,255,0.15)' : 'transparent',
              color: viewTab === t.key ? '#00d4ff' : 'rgba(255,255,255,0.5)',
              transition: 'all 0.2s',
            }}
          >{t.label}</button>
        ))}
      </div>

      {/* Anomalies View */}
      {viewTab === 'anomalies' && (
        <div className="glass" style={{ padding: 20 }}>
          {anomalies.length === 0 ? (
            <p style={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center', padding: 20 }}>No unresolved anomalies</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {anomalies.map(a => (
                <div key={a.id} className="glass" style={{ padding: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{
                    padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                    background: `${SEV_COLORS[a.severity] || '#6b7280'}20`,
                    color: SEV_COLORS[a.severity] || '#6b7280',
                  }}>{a.severity}</span>
                  <span style={{ fontSize: 12, fontWeight: 600, minWidth: 80 }}>{a.service_name}</span>
                  <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', flex: 1 }}>
                    {a.metric_name}: {fmt(a.metric_value, 2)} (baseline: {fmt(a.baseline_value, 2)}, {fmt(a.deviation, 1)}x)
                  </span>
                  <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>{new Date(a.detected_at).toLocaleString()}</span>
                  <button
                    onClick={() => handleResolveAnomaly(a.id)}
                    disabled={resolvingId === a.id}
                    style={{
                      padding: '4px 10px', borderRadius: 6, fontSize: 11, cursor: 'pointer',
                      background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', color: '#22c55e',
                      opacity: resolvingId === a.id ? 0.5 : 1,
                    }}
                  >{resolvingId === a.id ? '...' : 'Resolve'}</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Incidents View */}
      {viewTab === 'incidents' && (
        <div className="glass" style={{ padding: 20 }}>
          {incidents.length === 0 ? (
            <p style={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center', padding: 20 }}>No incidents recorded</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {incidents.map(inc => (
                <div key={inc.id} className="glass" style={{ padding: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                    <span style={{
                      padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                      background: `${SEV_COLORS[inc.severity] || '#6b7280'}20`,
                      color: SEV_COLORS[inc.severity] || '#6b7280',
                    }}>{inc.severity}</span>
                    <span style={{
                      padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                      background: `${STATUS_COLORS[inc.status] || '#6b7280'}20`,
                      color: STATUS_COLORS[inc.status] || '#6b7280',
                    }}>{inc.status}</span>
                    <span style={{ fontWeight: 600, fontSize: 13, flex: 1 }}>{inc.title}</span>
                    <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>{new Date(inc.detected_at).toLocaleString()}</span>
                  </div>
                  {inc.probable_cause && (
                    <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 4 }}>
                      Cause: {inc.probable_cause}
                    </p>
                  )}
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {inc.services_affected.map(s => (
                      <span key={s} style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.6)' }}>
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Dependencies View */}
      {viewTab === 'dependencies' && (
        <div className="glass" style={{ padding: 20 }}>
          {dependencies.length === 0 ? (
            <p style={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center', padding: 20 }}>No dependencies tracked</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                    <th style={{ textAlign: 'left', padding: '8px 12px', color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>Component</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>Category</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>Current</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>Latest</th>
                    <th style={{ textAlign: 'center', padding: '8px 12px', color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {dependencies.map(d => (
                    <tr key={d.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                      <td style={{ padding: '8px 12px', fontWeight: 500 }}>{d.component}</td>
                      <td style={{ padding: '8px 12px', color: 'rgba(255,255,255,0.6)' }}>{d.category}</td>
                      <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: 12 }}>{d.current_version}</td>
                      <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: 12 }}>{d.latest_version || '—'}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                        <span style={{
                          padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                          background: `${STATUS_COLORS[d.status] || '#6b7280'}20`,
                          color: STATUS_COLORS[d.status] || '#6b7280',
                        }}>{d.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Probes View */}
      {viewTab === 'probes' && (
        <div className="glass" style={{ padding: 20 }}>
          {probes.length === 0 ? (
            <p style={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center', padding: 20 }}>No probes configured</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
              {probes.map(p => (
                <div key={p.id} className="glass" style={{ padding: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{p.name}</span>
                    <span style={{
                      width: 8, height: 8, borderRadius: '50%',
                      background: p.is_active ? (p.last_status === 'pass' ? '#22c55e' : '#ef4444') : '#6b7280',
                    }} />
                  </div>
                  {p.description && (
                    <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 8 }}>{p.description}</p>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
                    <span>Success: {fmtPct(p.success_rate_7d, 0)}</span>
                    <span>{p.last_run_at ? new Date(p.last_run_at).toLocaleString() : 'Never run'}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OpsAgentPanel;
